import type { IdleMaterial, IdleSku } from '@api/replenishment-engine';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { Badge } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { useMemo, type FC, type ReactElement } from 'react';

import { dayLabel, whenLabel } from './dates';
import { TYPE_LABEL } from './status';
import { Lines } from './tables';

const n = (v: number, maxDecimals = 0): string => formatCount(v, { maxDecimals });

type Tone = 'red' | 'green' | 'orange' | undefined;

/** Why the window has nothing for this SKU: one line and a detail, from the row's reason. */
const skuWhy = (r: IdleSku, kind: 'PRODUCTION' | 'FG', today: string): { text: string; sub: string; tone: Tone } => {
    switch (r.reason) {
        case 'NO_DIP':
            return { text: 'never dips in the horizon', sub: `${r.inboundOnBook > 0 ? `${n(r.onHand)} on hand and ${n(r.inboundOnBook)} on the book${r.firstInboundDate ? ` from ${dayLabel(r.firstInboundDate)}` : ''} keep it` : 'stock stays'} above the ${r.levelDays}-day level${r.horizonDays ? ` for the ${r.horizonDays} days the run looked ahead` : ''}`, tone: 'green' };
        case 'DIP_AFTER_WINDOW':
            return { text: `first dip ${dayLabel(r.dipDate)}`, sub: 'past the furthest window an objective asks about', tone: 'green' };
        case 'COVERED':
            return { text: `dip ${dayLabel(r.dipDate)} already covered`, sub: `a landing on the book (approved or in transit) arrives ${dayLabel(r.coveringLandDate)}`, tone: 'green' };
        case 'UNSOURCEABLE':
            return { text: r.dipDate ? `dips ${dayLabel(r.dipDate)} · no route` : 'no route', sub: 'no factory, vendor or SFG route is mapped for it', tone: 'red' };
        case 'LATER':
        case 'EARLIER': {
            const when = kind === 'PRODUCTION' ? (r.next?.startDate ?? r.next?.placeBy ?? null) : (r.next?.placeBy ?? null);
            return {
                text: r.reason === 'LATER' ? 'planned for a later window' : 'placed before this window',
                sub: r.next ? `${n(r.next.qty)} ${kind === 'PRODUCTION' ? 'starts' : 'placed'} ${whenLabel(when, today)} · lands ${dayLabel(r.next.landDate)} · dips ${dayLabel(r.dipDate)}` : '',
                tone: 'orange',
            };
        }
        default:
            return { text: 'nothing to do', sub: '', tone: undefined };
    }
};

/**
 * The SKUs of the view's kind with nothing in its window, each with why: the filter the
 * brand flips to check that a quiet SKU is quiet for a reason. A standard sheet, read-only;
 * a row opens the SKU's story.
 */
export const IdleSkuSheet: FC<{ rows: IdleSku[]; kind: 'PRODUCTION' | 'FG'; today: string; onOpenSku: (productId: string) => void }> = ({ rows, kind, today, onOpenSku }) => {
    const columns = useMemo<DataSheetColumn<IdleSku>[]>(
        () => [
            { key: 'sku', header: 'SKU', width: 220, sticky: true, sortable: true, valueAccessor: (r) => r.sku, render: (_v, r): ReactElement => <Lines lines={[{ text: r.sku, strong: true, sub: r.productName ?? r.productId }]} /> },
            { key: 'onHand', header: 'On hand', width: 130, sortable: true, align: 'right', valueAccessor: (r) => r.onHand, render: (_v, r): ReactElement => <Lines lines={[{ text: n(r.onHand), sub: `${n(r.drr, 1)} a day` }]} /> },
            { key: 'cover', header: 'Cover', width: 120, sortable: true, align: 'right', valueAccessor: (r) => r.coverDays ?? Number.POSITIVE_INFINITY, render: (_v, r): ReactElement => <Lines lines={[{ text: r.coverDays == null ? 'no demand' : `${n(r.coverDays)} days`, sub: `level ${r.levelDays} days` }]} /> },
            { key: 'inbound', header: 'On the book', width: 150, sortable: true, align: 'right', valueAccessor: (r) => r.inboundOnBook, render: (_v, r): ReactElement => <Lines lines={[{ text: r.inboundOnBook > 0 ? n(r.inboundOnBook) : '—', sub: r.firstInboundDate ? `from ${dayLabel(r.firstInboundDate)}` : '' }]} /> },
            { key: 'dip', header: 'Dips', width: 130, sortable: true, valueAccessor: (r) => r.dipDate ?? '9999', render: (_v, r): ReactElement => <Lines lines={[{ text: r.dipDate ? whenLabel(r.dipDate, today) : 'never', sub: r.zeroDate ? `zero ${dayLabel(r.zeroDate)}` : '' }]} /> },
            {
                key: 'why',
                header: 'Why nothing here',
                width: 440,
                sortable: true,
                valueAccessor: (r) => r.reason,
                render: (_v, r): ReactElement => {
                    const w = skuWhy(r, kind, today);
                    return <Lines lines={[{ text: w.text, strong: true, sub: w.sub, tone: w.tone }]} />;
                },
            },
        ],
        [kind, today],
    );
    return <DataSheet<IdleSku> data={rows} columns={columns} getRowKey={(r) => r.productId} rowHeight={52} hideHeaderBar maxHeight="auto" onRowClick={(r) => onOpenSku(r.productId)} />;
};

/** Why nothing is placed for this material in the window. */
const materialWhy = (r: IdleMaterial, today: string): { text: string; sub: string; tone: Tone } => {
    const u = r.unit ? ` ${r.unit}` : '';
    switch (r.reason) {
        case 'NO_DRAW':
            return { text: 'no run in this window draws it', sub: r.drawLater > 0 ? `${n(r.drawLater, 1)}${u} drawn by later runs` : 'no planned run draws it at all', tone: 'green' };
        case 'FLOOR_COVERS':
            return { text: 'the floor covers it', sub: `${n(r.drawInWindow, 1)}${u} drawn by ${r.runsInWindow} run${r.runsInWindow === 1 ? '' : 's'} · ${n(r.onHand, 1)}${u} on the floor`, tone: 'green' };
        case 'SHORT':
            return { text: `short ${n(r.shortInWindow, 1)}${u}`, sub: 'no vendor or lane can bring it, so nothing could be ordered', tone: 'red' };
        case 'ORDER_LATER':
            return { text: 'its order is proposed for a later window', sub: r.next ? `${n(r.next.qty, 1)}${u} ${TYPE_LABEL[r.next.type]?.toLowerCase() ?? r.next.type} · place by ${whenLabel(r.next.placeBy, today)} · lands ${dayLabel(r.next.landDate)}${r.next.vendorName ? ` · ${r.next.vendorName}` : ''}` : '', tone: 'orange' };
        case 'ON_BOOK':
            return { text: `a${r.next ? `n ${r.next.status.toLowerCase()}` : ''} ${r.next ? (TYPE_LABEL[r.next.type]?.toLowerCase() ?? 'line') : 'line'} already brings it`, sub: r.next ? `${n(r.next.qty, 1)}${u} lands ${dayLabel(r.next.landDate)}${r.next.vendorName ? ` · ${r.next.vendorName}` : ''}` : '', tone: 'green' };
        default:
            return { text: 'nothing to place', sub: '', tone: undefined };
    }
};

/**
 * The materials the planned runs draw on that need no order or transfer in the window: what
 * is on the floor, what the window's runs draw, how long the floor lasts, and why nothing is
 * placed. A row opens the material's story at its factory.
 */
export const IdleMaterialSheet: FC<{ rows: IdleMaterial[]; today: string; onOpenMaterial?: (materialId: string, siteId: string) => void }> = ({ rows, today, onOpenMaterial }) => {
    const columns = useMemo<DataSheetColumn<IdleMaterial>[]>(
        () => [
            {
                key: 'material',
                header: 'Material',
                width: 240,
                sticky: true,
                sortable: true,
                valueAccessor: (r) => r.materialName ?? r.materialId,
                render: (_v, r): ReactElement => (
                    <Lines
                        lines={[
                            {
                                text: (
                                    <>
                                        {r.materialName ?? r.materialId}
                                        {r.stage && (
                                            <Badge size="xs" variant="outline" color="gray" tt="none" ml={6}>
                                                stage
                                            </Badge>
                                        )}
                                    </>
                                ),
                                strong: true,
                                sub: r.materialName ? r.materialId : '',
                            },
                        ]}
                    />
                ),
            },
            { key: 'onHand', header: 'On the floor', width: 140, sortable: true, align: 'right', valueAccessor: (r) => r.onHand, render: (_v, r): ReactElement => <Lines lines={[{ text: `${n(r.onHand, 1)}${r.unit ? ` ${r.unit}` : ''}`, sub: r.sites.length === 1 ? `at ${r.sites[0]}` : `across ${r.sites.length} factories` }]} /> },
            { key: 'inbound', header: 'On the book', width: 130, sortable: true, align: 'right', valueAccessor: (r) => r.inboundOnBook, render: (_v, r): ReactElement => <Lines lines={[{ text: r.inboundOnBook > 0 ? n(r.inboundOnBook, 1) : '—' }]} /> },
            { key: 'draw', header: 'Draw in window', width: 160, sortable: true, align: 'right', valueAccessor: (r) => r.drawInWindow, render: (_v, r): ReactElement => <Lines lines={[{ text: r.drawInWindow > 0 ? n(r.drawInWindow, 1) : '—', sub: r.runsInWindow > 0 ? `${r.runsInWindow} run${r.runsInWindow === 1 ? '' : 's'}${r.drawLater > 0 ? ` · ${n(r.drawLater, 1)} later` : ''}` : r.drawLater > 0 ? `${n(r.drawLater, 1)} by later runs` : '' }]} /> },
            { key: 'covers', header: 'Floor lasts', width: 150, sortable: true, valueAccessor: (r) => r.runsOutOn ?? '9999', render: (_v, r): ReactElement => <Lines lines={[{ text: r.runsOutOn ? `runs out ${whenLabel(r.runsOutOn, today)}` : 'through the horizon', sub: r.runsOutOn ? 'against every planned draw' : '', tone: r.runsOutOn ? 'orange' : 'green' }]} /> },
            {
                key: 'why',
                header: 'Why nothing here',
                width: 420,
                sortable: true,
                valueAccessor: (r) => r.reason,
                render: (_v, r): ReactElement => {
                    const w = materialWhy(r, today);
                    return <Lines lines={[{ text: w.text, strong: true, sub: w.sub, tone: w.tone }]} />;
                },
            },
        ],
        [today],
    );
    return <DataSheet<IdleMaterial> data={rows} columns={columns} getRowKey={(r) => r.materialId} rowHeight={52} hideHeaderBar maxHeight="auto" onRowClick={(r) => onOpenMaterial?.(r.materialId, r.sites[0] ?? '')} />;
};
